/* eslint-disable jsx-a11y/anchor-is-valid */
import { gql, useQuery } from "@apollo/client";
import { faImagePolaroid } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, Typography, useTheme } from "@material-ui/core";
import { FileInfo, Widget, WidgetAPI } from "@uploadcare/react-widget";
import { usePollingIntervalConfig } from "client/utils/polling";
import clsx from "clsx";
import Loading from "components/legacy/Loading/Loading";
import { GenerateUploadcareSignature } from "generated/schemaTypes";
import { useRef, useState } from "react";
import { ResourceCustomTheme } from "styles/config";

import styles from "./DropzoneWithPreview.module.scss";

const GENERATE_SIGNATURE_FOR_UPLOADER = gql`
  query GenerateUploadcareSignatureForDropzone {
    uploadcareSignature {
      signature
      expire
    }
  }
`;

type DropzoneWithPreviewProps = {
  photoUrl: string;
  setPhotoUrl: (x: string) => void;
  previewStyle?: "cover" | "contain";
  direction?: "row" | "column";
  disabled?: boolean;
};

const validImageTypes = [
  "image/jpeg",
  "image/gif",
  "image/png",
  "image/bmp",
  "image/webp",
  "image/heic",
  "image/heif",
];

function DropzoneWithPreview({
  photoUrl,
  setPhotoUrl,
  previewStyle = "cover",
  direction = "row",
  disabled,
}: DropzoneWithPreviewProps) {
  const isVertical = direction === "column";
  const theme = useTheme<ResourceCustomTheme>();
  const widgetApi = useRef<WidgetAPI>(null);
  const [error, setError] = useState("");

  const { uploadCareSignature: pollInterval } = usePollingIntervalConfig();
  const { data, loading } = useQuery<GenerateUploadcareSignature>(
    GENERATE_SIGNATURE_FOR_UPLOADER,
    {
      fetchPolicy: "network-only",
      pollInterval,
    }
  );
  const signature = data?.uploadcareSignature;
  const css = `
  .uploadcare--button_primary {
    background-color: ${theme.colors.RoyalPurple()} !important;
    border-color: ${theme.colors.RoyalPurple()} !important;
  }

  .uploadcare--widget__button,
      .uploadcare--widget__dragndrop-area,
      .uploadcare--progress,
      .uploadcare--widget__text {
        display: none;
      }
  `;

  const onFileUpload = (file: FileInfo) => {
    if (file.cdnUrl) {
      setPhotoUrl(file.cdnUrl);
    } else {
      setError("Something went wrong uploading your file");
    }
  };

  const imagesOnly = (fileInfo: FileInfo) => {
    // This will be called again once the mime type is determined so ignore for now
    if (!fileInfo.mimeType) {
      return;
    }

    if (!validImageTypes.includes(fileInfo.mimeType)) {
      throw new Error("image");
    }
  };

  function InnerComponent() {
    if (loading) return <Loading />;
    if (error) {
      return (
        <div className={styles.dropzoneComponent}>
          <Typography style={{ color: "red" }}> {error} </Typography>
        </div>
      );
    }
    return (
      <div className={styles.dropzoneComponent}>
        <>
          <Typography className={styles.dropzoneText}>
            {disabled ? (
              <>Only an admin can change this image</>
            ) : (
              <>
                <Link>choose an image</Link>&nbsp;by url or file upload
              </>
            )}
          </Typography>
        </>
      </div>
    );
  }

  return (
    <div
      className={clsx({
        [styles.formContainer]: !isVertical,
      })}
    >
      <style>{css}</style>
      <div
        className={clsx({
          [styles.previewContainer]: !isVertical,
          [styles.verticalPreviewContainer]: isVertical,
        })}
      >
        {photoUrl === "" ? (
          <FontAwesomeIcon
            className={styles.icon}
            icon={faImagePolaroid}
            size="2x"
          />
        ) : (
          <div
            className={styles.preview}
            style={{
              backgroundImage: `url(${photoUrl})`,
              backgroundSize: previewStyle,
            }}
          />
        )}
      </div>
      {signature && (
        <div
          tabIndex={0}
          onKeyUp={() => {
            if (disabled) return;
            widgetApi.current?.openDialog("url");
          }}
          className={clsx({
            [styles.dropzone]: !isVertical,
            [styles.verticalDropzone]: isVertical,
            [styles.dragError]: error,
          })}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          role="button"
          onClick={() => {
            if (disabled) return;
            widgetApi.current?.openDialog("url");
          }}
        >
          <InnerComponent />
          <Widget
            ref={widgetApi}
            publicKey={process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY ?? ""}
            preloader={null}
            onChange={onFileUpload}
            secureSignature={signature.signature}
            secureExpire={Number(signature.expire)}
            imagesOnly
            validators={[imagesOnly]}
            inputAcceptTypes={validImageTypes.join(",")}
            previewStep
            crop="free"
            tabs="file url dropbox"
          />
        </div>
      )}
    </div>
  );
}
export default DropzoneWithPreview;
